import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import {
  HttpError,
  useOne,
  useParse,
  useTranslation,
  useUpdate,
} from "@refinedev/core";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { isEmpty } from "lodash";
import {
  ISimpleEventDetail,
  ITicketPhasesForm,
  ITicketsResponseData,
  Phase,
  SupportedLanguage,
  TicketType,
} from "interfaces";
import Button from "components/button";
import { SwitchButton } from "components/switchButton";
import { CreateEventStep } from "interfaces";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TicketPhase } from "./ticketPhase";
import { ConfirmModal } from "components/confirmModal";
import { Tickets } from "./tickets";
import { GroupTickets } from "./groupTickets";
import { languageSchema } from "components/shared/event/schema";
import { FullScreenLoading } from "components/fullScreenLoading";
import { endpointService } from "components/shared/services/endpointService";
import { Label } from "components/label";
import i18n from "utills/i18n";

enum Section {
  TICKET_PHASES = "ticket_phases",
  INDIVIDUAL_TICKET = "tickets",
  GROUP_TICKET = "group_tickets",
  NONE = "none",
}

const CreateTicketPhaseSchema = (languages: SupportedLanguage[]) => {
  const ticketSchema = z.array(
    z.object({
      category: languageSchema(languages),
      type_name: languageSchema(languages),
      short_description: languageSchema(languages),
      selling_min_qty: z.union([
        z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
          })
          .min(0, {
            message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
          }),
      ]),
      selling_max_qty: z.union([
        z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
          })
          .min(0, {
            message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
          }),
      ]),
      selling_type: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      price_type: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      original_price: z.union([
        z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
          })
          .min(0, {
            message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
          }),
      ]),
      price: z.union([
        z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        z
          .number({
            invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
          })
          .min(0, {
            message: i18n.t("validation.errorMessage.minNum", { min: 0 }),
          }),
      ]),
      sell_from: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      sell_to: z
        .string()
        .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
      attendant_template: z.array(
        z.object({
          template_id: z.string(),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional(),
          }),
        })
      ),
      is_accept_edit_participant_info: z.boolean(),
      is_accept_upgrade_ticket: z.boolean(),
      is_accept_transfer_ticket: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.union([
        z
          .string()
          .min(1, i18n.t("validation.errorMessage.thisFieldIsRequired")),
        z.number({
          invalid_type_error: i18n.t("validation.errorMessage.mustBeANumber"),
        }),
      ]),
    })
  );

  return z.object({
    ticket_phases: z.array(
      z.object({
        name: languageSchema(languages),
        is_active: z.boolean(),
        hide_in_ui: z.boolean(),
      })
    ),
    tickets: ticketSchema,
    group_tickets: ticketSchema,
  });
};

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  submit: () => {},
};

export const TicketAndPhase = (props: {
  eventDetailData: ISimpleEventDetail;
  refetchEventDetail: () => void;
}) => {
  const parse = useParse();
  const { params } = parse();

  const {
    data,
    isLoading: isLoading,
    refetch: fetchTickets,
  } = useOne<ITicketsResponseData>({
    resource: endpointService.getEventEndpoint("getTickets", {
      eventId: params?.id ?? "",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const defaultTicketsData = data?.data ?? null;

  return (
    <>
      <TicketAndPhaseForm
        defaultTicketsData={defaultTicketsData}
        fetchTickets={fetchTickets}
        {...props}
      />
      {isLoading ? <FullScreenLoading /> : <></>}
    </>
  );
};

const TicketAndPhaseForm = ({
  eventDetailData,
  defaultTicketsData,
  fetchTickets,
  refetchEventDetail,
}: {
  eventDetailData: ISimpleEventDetail;
  defaultTicketsData: ITicketsResponseData | null;
  fetchTickets: () => void;
  refetchEventDetail: () => void;
}) => {
  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);
  const [enablePhaseModalConfig, setEnablePhaseModalConfig] = useState(
    INITIAL_ACTION_MODAL_CONFIG
  );

  const { mutate: updateMutation } = useUpdate();

  const parse = useParse();
  const { params } = parse();

  const { translate } = useTranslation();

  const methods = useForm<ITicketPhasesForm, HttpError, ITicketPhasesForm>({
    defaultValues: {
      ticket_phases: defaultTicketsData?.ticket_phases ?? [],
      tickets: defaultTicketsData?.tickets ?? [],
      group_tickets: defaultTicketsData?.group_tickets ?? [],
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        CreateTicketPhaseSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },
  });

  const { data: ticketPhaseData, refetch: refetchTicketPhases } = useOne<
    Phase[],
    HttpError
  >({
    resource: endpointService.getEventEndpoint("getPhases", {
      eventId: eventDetailData.id ?? "",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const ticketPhases = ticketPhaseData?.data ?? null;

  useEffect(() => {
    if (ticketPhases) {
      methods.setValue("ticket_phases", ticketPhases ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketPhases]);

  useEffect(() => {
    if (defaultTicketsData) {
      methods.setValue("tickets", defaultTicketsData.tickets ?? []);
      methods.setValue("group_tickets", defaultTicketsData.group_tickets ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTicketsData]);

  const _toggleActiveTicketPhase = (isActive: boolean) => {
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("toggleTicketPhaseStatus", {
          eventId: eventDetailData.id ?? "",
          status: isActive ? "enable" : "disable",
        }),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success(translate("toastMessage.updatedSuccessfully"));
          refetchTicketPhases();
          fetchTickets();
          refetchEventDetail();
          if (!isActive && openSectionId === Section.TICKET_PHASES) {
            setOpenSectionId(Section.NONE);
          }
        },
      }
    );
  };

  const _toggleActiveIsPaymentGroup = (isActive: boolean) => {
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("toggleGroupTicketStatus", {
          eventId: eventDetailData.id ?? "",
          status: isActive ? "enable" : "disable",
        }),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success(translate("toastMessage.updatedSuccessfully"));
          refetchTicketPhases();
          refetchEventDetail();
        },
      }
    );
  };

  const _togglePublishTicketPhases = (
    isActive: boolean,
    ticketType: TicketType
  ) => {
    updateMutation(
      {
        resource: endpointService.getEventEndpoint(
          "togglePublishPhasesStatus",
          {
            eventId: eventDetailData.id ?? "",
            ticketType,
            status: isActive ? "publish" : "unpublish",
          }
        ),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success(translate("toastMessage.updatedSuccessfully"));
          refetchTicketPhases();
          refetchEventDetail();
        },
      }
    );
  };

  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    return setOpenSectionId(
      openSectionId === sectionId ? Section.NONE : sectionId
    );
  };

  const _onCloseEnablePhaseModal = () => {
    setEnablePhaseModalConfig(INITIAL_ACTION_MODAL_CONFIG);
  };

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-full gap-y-2 sm:gap-y-4">
        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0 text-sm">
              <h3 className="text-base font-semibold leading-6">
                {translate("eventSetup.phaseAndTicket.ticketPhases")}
              </h3>
              <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                <Label
                  id="ticket-phases"
                  name={translate("eventSetup.phaseAndTicket.togglePhaseLabel")}
                  hint={translate("eventSetup.phaseAndTicket.togglePhaseNote")}
                  className="m-0 font-normal"
                />
                <SwitchButton
                  checked={!isEmpty(methods.watch("ticket_phases") ?? [])}
                  disabled={
                    !isEmpty(methods.watch("ticket_phases") ?? []) &&
                    (!isEmpty(methods.watch("tickets")) ||
                      !isEmpty(methods.watch("group_tickets")))
                  }
                  onChange={(isCheck) => {
                    if (isCheck) {
                      setEnablePhaseModalConfig({
                        open: true,
                        submit: () => _toggleActiveTicketPhase(true),
                      });
                    } else {
                      _toggleActiveTicketPhase(false);
                    }
                  }}
                />
              </div>
              <p className="mt-2">
                {translate("eventSetup.phaseAndTicket.phaseNote")}
              </p>
            </div>
            {!isEmpty(methods.watch("ticket_phases")) ? (
              <button
                onClick={() => _handleSubmitSection(Section.TICKET_PHASES)}
                type="button"
              >
                {openSectionId === Section.TICKET_PHASES ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            ) : (
              <></>
            )}
          </div>
          {openSectionId === Section.TICKET_PHASES ? (
            <TicketPhase
              eventDetailData={eventDetailData}
              fetchTicketPhases={refetchTicketPhases}
            />
          ) : (
            <></>
          )}
        </div>

        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0">
              <h3 className="text-base font-semibold leading-6">
                {translate("eventSetup.phaseAndTicket.individualTickets")}
              </h3>
              {!isEmpty(methods.watch("ticket_phases") ?? []) ? (
                <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="public-all-ticket-phases"
                    name={translate(
                      "eventSetup.phaseAndTicket.publicAllTicketPhases"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.publicAllTicketPhasesNote"
                    )}
                    className="m-0 font-normal"
                  />
                  <SwitchButton
                    checked={
                      eventDetailData.is_publish_all_ticket_phases ?? false
                    }
                    onChange={(isChecked) =>
                      _togglePublishTicketPhases(
                        isChecked,
                        TicketType.INDIVIDUAL
                      )
                    }
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
            <button
              onClick={() => _handleSubmitSection(Section.INDIVIDUAL_TICKET)}
              type="button"
            >
              {openSectionId === Section.INDIVIDUAL_TICKET ? (
                <MinusCircleIcon className="w-6 h-6 text-brand-500" />
              ) : (
                <PlusCircleIcon className="w-6 h-6 text-brand-500" />
              )}
            </button>
          </div>
          {openSectionId === Section.INDIVIDUAL_TICKET ? (
            <Tickets
              eventDetailData={eventDetailData}
              ticketPhases={ticketPhases ?? []}
              fetchEventDetail={refetchEventDetail}
            />
          ) : (
            <></>
          )}
        </div>

        <div className="box-container p-0">
          <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 shrink-0">
              <h3 className="text-base font-semibold leading-6">
                {translate("eventSetup.phaseAndTicket.groupTickets")}
              </h3>
              <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                <Label
                  id="sell-group-tickets"
                  name={translate(
                    "eventSetup.phaseAndTicket.doYouSellGroupTickets"
                  )}
                  className="m-0 font-normal"
                />
                <SwitchButton
                  checked={eventDetailData.is_payment_group}
                  onChange={_toggleActiveIsPaymentGroup}
                />
              </div>
              {!isEmpty(methods.watch("ticket_phases") ?? []) ? (
                <div className="mt-4 text-sm flex flex-wrap items-center gap-x-4">
                  <Label
                    id="public-all-group-ticket-phases"
                    name={translate(
                      "eventSetup.phaseAndTicket.publicAllGroupTicketPhases"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.publicAllGroupTicketPhasesNote"
                    )}
                    className="m-0 font-normal"
                  />
                  <SwitchButton
                    checked={
                      eventDetailData.is_publish_all_ticket_phases_group ??
                      false
                    }
                    onChange={(isChecked) =>
                      _togglePublishTicketPhases(isChecked, TicketType.GROUP)
                    }
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
            <button
              onClick={() => _handleSubmitSection(Section.GROUP_TICKET)}
              type="button"
            >
              {openSectionId === Section.GROUP_TICKET ? (
                <MinusCircleIcon className="w-6 h-6 text-brand-500" />
              ) : (
                <PlusCircleIcon className="w-6 h-6 text-brand-500" />
              )}
            </button>
          </div>
          {openSectionId === Section.GROUP_TICKET ? (
            <GroupTickets
              eventDetailData={eventDetailData}
              ticketPhases={ticketPhases ?? []}
              fetchEventDetail={refetchEventDetail}
            />
          ) : (
            <></>
          )}
        </div>

        <div
          className="w-full mt-auto px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
          style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
        >
          <div className="flex items-center justify-end gap-x-4">
            <Link
              to={`/events/sports/${params?.id}/edit?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`}
            >
              <Button className="w-auto min-w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                {translate("button.back")}
              </Button>
            </Link>
            <Link
              to={`/events/sports/${params?.id}/edit?step=${CreateEventStep.ADDITIONAL_SETUP}`}
            >
              <Button
                className="w-auto min-w-20 disabled:opacity-70"
                disabled={eventDetailData.current_step < params?.step}
              >
                {translate("button.next")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <ConfirmModal
        key="enable-phase"
        title={translate("eventSetup.phaseAndTicket.modal.togglePhase.title")}
        content={translate(
          "eventSetup.phaseAndTicket.modal.togglePhase.content"
        )}
        open={enablePhaseModalConfig.open}
        onConfirm={enablePhaseModalConfig.submit}
        onClose={_onCloseEnablePhaseModal}
      />
    </FormProvider>
  );
};
